import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import Request from 'redux/config/services/Request'
import FileUpload from 'core-system/FileUpload/FileUpload'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import { uploadReceipt } from './uploadFile'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import { useNavigate } from 'react-router-dom'
import { Locations } from 'shared/Router/Locations'
import styled from 'styled-components'
import Input from 'core-system/Input/Input'
import moment from 'moment'
import Dropdown from 'core-system/Dropdown/Dropdown'
import CircularProgress from '@mui/material/CircularProgress'

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`

const InfoBannerContainer = styled.div<{
  pageState: 'initial' | 'submitting' | 'success' | 'error'
}>`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  background-color: ${(props) =>
    props.pageState === 'success'
      ? palette.success.green200
      : props.pageState === 'error'
        ? palette.error.red200
        : palette.secondary.purple200};
  padding: 1rem;
  border-radius: 0.5rem;
  align-items: center;
`

const InfoBanner = ({
  pageState,
}: {
  pageState: 'initial' | 'submitting' | 'success' | 'error'
}) => {
  if (pageState === 'initial') {
    return null
  }
  if (pageState === 'submitting') {
    return (
      <InfoBannerContainer pageState={pageState}>
        <CircularProgress
          size={20}
          sx={{ color: palette.secondary.purple500 }}
        />
        Submitting...
      </InfoBannerContainer>
    )
  }
  if (pageState === 'success') {
    return (
      <InfoBannerContainer pageState={pageState}>
        Request Submitted Successfully!
      </InfoBannerContainer>
    )
  }
  if (pageState === 'error') {
    return (
      <InfoBannerContainer pageState={pageState}>
        Error Submitting Request, please try again.
      </InfoBannerContainer>
    )
  }
  return null
}

const StatusView = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const selectedCounty = location.state?.selectedCounty
  const { profileData } = useSelector(
    (state: AppState) => state.employeePlatform
  )
  const [file, setFile] = useState([])
  const [tripDistance, setTripDistance] = useState('0')
  const [reimbursementAmount, setReimbursementAmount] = useState('0')
  const [methodOfRideHome, setMethodOfRideHome] = useState('')
  const [pageState, setPageState] = useState('initial')

  const submitForm = async () => {
    setPageState('submitting')
    try {
      const response = await Request.post(
        '/mobile/core/program_requests/government_programs/',
        {
          program_type: 'alameda',
          form_data: {
            name: `${profileData.firstName} ${profileData.lastName}`,
            email: profileData.email,
            phone: '1234567890',
            how_got_to_work: 'Carpool',
            ride_method: 'Taxi',
            time_of_ride: moment().format('MM-DD-YYYY HH:mm:ss'),
            pickup_address: profileData.worksiteAddress,
            pickup_city: 'Alameda',
            dropoff_address: profileData.homeAddress,
            dropoff_city: 'Alameda',
            reason_for_ride: 'Your carpool or vanpool vehicle broke down',
            reimbursement_amount: reimbursementAmount,
            trip_distance: tripDistance,
            receipt: file[0].downloadUrl,
            paypal_email: profileData.email,
          },
        }
      )
      if (response.status === 200) {
        setPageState('success')
      }
    } catch (error) {
      console.error(error)
      setPageState('error')
    }
  }

  const uploadGovernmentReceipt = async (files: File[]) => {
    const response = await uploadReceipt(files[0])
    if (response.status === 'success') {
      setFile([
        {
          name: 'Receipt',
          downloadUrl: response.downloadUrl,
          type: files[0].type,
        },
      ])
    }
  }

  const isSubmitDisabled = () => {
    return (
      tripDistance === 0 ||
      reimbursementAmount === 0 ||
      methodOfRideHome === '' ||
      file.length === 0
    )
  }

  return (
    <div>
      <InfoBanner pageState={pageState} />
      <Text
        variant='action2'
        textColor={palette.text.secondary}
        marginTop='1rem'
        marginBottom='0.5rem'
      >
        Trip Distance (Miles)
      </Text>
      <Input
        onChange={(e) => setTripDistance(e.target.value)}
        disabled={pageState === 'submitting'}
      />
      <Text
        variant='action2'
        textColor={palette.text.secondary}
        marginTop='1rem'
        marginBottom='0.5rem'
      >
        Reimbursement Amount ($)
      </Text>
      <Input
        onChange={(e) => setReimbursementAmount(e.target.value)}
        disabled={pageState === 'submitting'}
      />
      <Text
        variant='action2'
        textColor={palette.text.secondary}
        marginTop='1rem'
        marginBottom='0.5rem'
      >
        Method of Ride Home
      </Text>
      <Dropdown
        items={[
          {
            id: 'Taxi',
            text: 'Taxi',
          },
          {
            id: 'Rental Car',
            text: 'Rental Car',
          },
          {
            id: 'Car share (e.g. Zipcar, City Carshare)',
            text: 'Car share (e.g. Zipcar, City Carshare)',
          },
          {
            id: 'Public Transit',
            text: 'Public Transit',
          },
          {
            id: 'Transportation Network Company (e.g. Uber, Lyft)',
            text: 'Transportation Network Company (e.g. Uber, Lyft)',
          },
        ]}
        active={methodOfRideHome}
        itemCallback={({ id }) => setMethodOfRideHome(id)}
        width='100%'
        disabled={pageState === 'submitting'}
      />
      <Text
        variant='action2'
        textColor={palette.text.secondary}
        marginTop='1rem'
        marginBottom='0.5rem'
      >
        Upload Receipt:
      </Text>
      <FileUpload
        placeholder='Drag and drop files here, or click to select files'
        acceptedFilesTypes={['.png', '.jpeg', '.pdf']}
        placeholderSubtitle='PDF, JPG, PNG'
        onChange={setFile}
        files={file}
        onError={() => null}
        uploadFiles={uploadGovernmentReceipt}
      />
      <ButtonsContainer>
        <PrimaryButton
          onClick={submitForm}
          disabled={isSubmitDisabled()}
          isLoading={pageState === 'submitting'}
        >
          Submit
        </PrimaryButton>
        <SecondaryButton
          onClick={() => navigate(Locations.EmployeeGRH.Overview)}
        >
          Cancel
        </SecondaryButton>
      </ButtonsContainer>
    </div>
  )
}

export default StatusView
